import React from "react";
import { MessageSquare, Paperclip, Download } from "lucide-react";
import Card from "components/card";

const TicketReplyCard = ({ 
    trp_message, 
    created_at_ago,
    department,
    isCurrentUser,
    isCustomer,
    attachment,
    user_name,
    variant = 'it-support'
}) => {
    
    const BACKEND_URL = 'http://127.0.0.1:8000';

    // Configuration based on variant
    const config = {
        'it-support': {
            customerIconClass: 'bg-purple-100 text-purple-700',
            staffIconClass: 'bg-blue-100 text-blue-700',
            defaultIconClass: 'bg-blue-100 text-blue-700',
            labelMap: {
                currentCustomer: "Your Reply",
                currentStaff: "Your Response",
                customer: "Customer Reply",
                default: "Department Response"
            }
        },
        'hr-support': {
            customerIconClass: 'bg-pink-100 text-pink-700',
            staffIconClass: 'bg-pink-100 text-pink-700',
            defaultIconClass: 'bg-pink-100 text-pink-700',
            labelMap: {
                currentCustomer: "Your Reply",
                currentStaff: "Your Response",
                customer: "Customer Reply",
                default: "People & Culture Team"
            }
        },
        'partner-service': {
            customerIconClass: 'bg-purple-100 text-purple-700',
            staffIconClass: 'bg-purple-100 text-purple-700',
            defaultIconClass: 'bg-purple-100 text-purple-700',
            labelMap: {
                currentCustomer: "Your Reply",
                currentStaff: "Your Response",
                customer: "Customer Reply",
                default: "Partner & Service Team"
            }
        }
    };

    const currentConfig = config[variant] || config['it-support'];

    const getReplyLabel = () => {
        if (isCurrentUser && isCustomer) return currentConfig.labelMap.currentCustomer;
        if (isCurrentUser && !isCustomer) return currentConfig.labelMap.currentStaff;
        if (isCustomer) return currentConfig.labelMap.customer;
        
        // For HR, use user_name if available, otherwise default
        if (variant === 'hr-support') {
            return user_name || currentConfig.labelMap.default;
        }
        
        return `${department} ${currentConfig.labelMap.default}`;
    };

    const getIconClass = () => {
        if (isCustomer && variant === 'it-support') return currentConfig.customerIconClass;
        return currentConfig.defaultIconClass;
    };

    const isImage = (filename) => {
        if (!filename) return false;
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'heic', 'heif'];
        const ext = filename.split('.').pop()?.toLowerCase();
        return imageExtensions.includes(ext);
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown size';
        const units = ['B', 'KB', 'MB', 'GB'];
        let i = 0;
        let size = bytes;
        while (size >= 1024 && i < units.length - 1) {
            size /= 1024;
            i++;
        }
        return `${size.toFixed(2)} ${units[i]}`;
    };

    // For HR support that doesn't have attachment data structure, convert if needed
    const attachmentData = variant === 'hr-support' && attachment ? {
        att_id: attachment.id,
        att_filename: attachment.filename,
        att_filesize: attachment.size
    } : attachment;

    return (
        <Card className="bg-white text-slate-900 flex flex-col gap-2 rounded-xl border-2 border-slate-200 overflow-hidden dark-mode-card">
            <div className="p-4">
                <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-xl ${getIconClass()}`}>
                        <MessageSquare className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                            <div>
                                <p className="font-semibold text-[13px] title-text">
                                    {getReplyLabel()}
                                </p>
                            </div>
                            <span className="text-[11px] muted-text">{created_at_ago}</span>
                        </div>
                        <p className="text-[11px] muted-text whitespace-pre-wrap break-words">
                            {trp_message}
                        </p>
                    </div>
                </div>

                {attachmentData && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                        {isImage(attachmentData.att_filename) ? (
                            <div className="space-y-2">
                                <img 
                                    src={`${BACKEND_URL}/download/attachment/${attachmentData.att_id}`}
                                    alt={attachmentData.att_filename}
                                    className="max-w-full max-h-48 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => window.open(`${BACKEND_URL}/download/attachment/${attachmentData.att_id}`, '_blank')}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) {
                                            e.target.nextSibling.classList.remove('hidden');
                                        }
                                    }}
                                />
                                <div className="hidden text-xs text-red-500">
                                    Failed to load image
                                </div>
                                <a 
                                    href={`${BACKEND_URL}/download/attachment/${attachmentData.att_id}`}
                                    download
                                    className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800"
                                >
                                    <Download className="w-3 h-3" />
                                    Download {attachmentData.att_filename} ({formatFileSize(attachmentData.att_filesize)})
                                </a>
                            </div>
                        ) : (
                            <a 
                                href={`${BACKEND_URL}/download/attachment/${attachmentData.att_id}`}
                                download
                                className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                            >
                                <Paperclip className="w-4 h-4 text-slate-500" />
                                <div className="flex-1">
                                    <p className="text-[12px] font-medium text-slate-700 truncate">
                                        {attachmentData.att_filename}
                                    </p>
                                    <p className="text-[10px] text-slate-500">
                                        {formatFileSize(attachmentData.att_filesize)}
                                    </p>
                                </div>
                                <Download className="w-3 h-3 text-slate-400" />
                            </a>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default TicketReplyCard;